import React, { useState, useEffect, useRef } from 'react';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';
import { put } from '@vercel/blob';
import Cropper from 'react-easy-crop';
import LocationPicker from './LocationPicker';
import './EditAdForm.css';

// Ensure API URL has protocol
const rawApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const API_BASE_URL = rawApiUrl.startsWith('http://') || rawApiUrl.startsWith('https://') 
  ? rawApiUrl 
  : `https://${rawApiUrl}`;

// Log API URL on module load
console.log('🔧 [EditAdForm] API_BASE_URL:', API_BASE_URL);
console.log('🔧 [EditAdForm] process.env.REACT_APP_API_URL:', process.env.REACT_APP_API_URL);

// Check if using old server URL and warn
if (API_BASE_URL.includes('gsradio-ads-server-o6bojj0dg') || API_BASE_URL.includes('gsradio-ads-server-lkhtujbjw')) {
  console.warn('⚠️  [WARNING] Using OLD server URL in EditAdForm!');
  console.warn('   Current URL:', API_BASE_URL);
  console.warn('   Expected: https://gsradio-ads-server-2dpcvvjut-eyal-shpits-projects-6e580987.vercel.app');
  console.warn('   Please update REACT_APP_API_URL in Vercel environment variables');
}

function EditAdForm({ ad, onSave, onCancel, loading = false }) {
  const isNewAd = ad && ad.isNew === true;
  
  // Initialize form state with ad data
  const [formData, setFormData] = useState({
    // Ad ID (only for new ads)
    id: '',
    
    // Business Information
    business_name: '',
    business_email: '',
    business_phone: '',
    business_website: '',
    business_description: '',
    
    // Visual Fields
    description: '',
    title: '',
    media_images: [], // Array of { image: string, name: string }
    video: '', // Video with subtitles
    video_no_subs: '', // Original video (without subtitles)
    vimeo: '',
    staticQRCODE: '',
    titleQRCODE: '',
    video_fullscreen: '', // Fullscreen video URL
    show_fullscreen_video: false, // Whether to show fullscreen video
    fullscreen_badge_image: '', // Badge image displayed on top of fullscreen video
    
    // Settings
    paid_until: '',
    debug: false,
    enabled: false,
    portrait: false,
    duration: 20, // Duration in seconds, default 20
    
    // Locations
    locations: [] // Array of { geolocation: string, meters: number }
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [convertingVoiceToText, setConvertingVoiceToText] = useState(false);
  const [generatingAISubtitles, setGeneratingAISubtitles] = useState(false);
  const [showImageCrop, setShowImageCrop] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [editingImageIndex, setEditingImageIndex] = useState(null); // null = new image, number = editing index
  const [creatingLinkly, setCreatingLinkly] = useState(false);
  const [uploadingBadgeImage, setUploadingBadgeImage] = useState(false);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const badgeImageInputRef = useRef(null);
  const mediaImagesRef = useRef([]); // Keep reference to media images for cleanup

  // Helper function to convert date to YYYY-MM-DD format for date input
  const formatDateForInput = (dateValue) => {
    if (!dateValue) return '';
    
    // If it's already in YYYY-MM-DD format, return as is
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }
    
    // Try to parse DD.MM.YY or DD.MM.YYYY format (e.g., "30.11.25" or "30.11.2025")
    if (typeof dateValue === 'string' && dateValue.includes('.')) {
      const parts = dateValue.split('.');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        let year = parseInt(parts[2], 10);
        
        // If year is 2 digits, assume 20XX
        if (year < 100) {
          year = 2000 + year;
        }
        
        // Validate date parts
        if (!isNaN(day) && !isNaN(month) && !isNaN(year) && 
            day >= 1 && day <= 31 && month >= 1 && month <= 12) {
          const date = new Date(year, month - 1, day);
          if (!isNaN(date.getTime())) {
            const formattedYear = date.getFullYear();
            const formattedMonth = String(date.getMonth() + 1).padStart(2, '0');
            const formattedDay = String(date.getDate()).padStart(2, '0');
            return `${formattedYear}-${formattedMonth}-${formattedDay}`;
          }
        }
      }
    }
    
    // Try to parse DD/MM/YY or DD/MM/YYYY format (e.g., "30/11/25" or "30/11/2025")
    if (typeof dateValue === 'string' && dateValue.includes('/')) {
      const parts = dateValue.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        let year = parseInt(parts[2], 10);
        
        // If year is 2 digits, assume 20XX
        if (year < 100) {
          year = 2000 + year;
        }
        
        // Validate date parts
        if (!isNaN(day) && !isNaN(month) && !isNaN(year) && 
            day >= 1 && day <= 31 && month >= 1 && month <= 12) {
          const date = new Date(year, month - 1, day);
          if (!isNaN(date.getTime())) {
            const formattedYear = date.getFullYear();
            const formattedMonth = String(date.getMonth() + 1).padStart(2, '0');
            const formattedDay = String(date.getDate()).padStart(2, '0');
            return `${formattedYear}-${formattedMonth}-${formattedDay}`;
          }
        }
      }
    }
    
    // Try to parse as Date
    try {
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        // Format as YYYY-MM-DD
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    } catch (e) {
      // If parsing fails, return empty string
    }
    
    return '';
  };

  // Populate form when ad changes
  useEffect(() => {
    if (ad) {
      // Clean up any existing blob URLs before loading new ad
      if (mediaImagesRef.current) {
        mediaImagesRef.current.forEach(item => {
          if (item && item.image && item.image.startsWith('blob:')) {
            URL.revokeObjectURL(item.image);
          }
        });
      }
      
      // Handle media - convert from object with numeric keys to array
      const mediaImages = [];
      if (ad.media) {
        // Handle both array and object formats
        if (Array.isArray(ad.media)) {
          ad.media.forEach((item) => {
            if (item && item.image) {
              mediaImages.push({
                image: item.image,
                name: item.name || ''
              });
            }
          });
        } else {
          // Object format with numeric keys (e.g., { 0: { image: '...', name: '...' }, 1: { ... } })
          Object.keys(ad.media).forEach((key) => {
            const item = ad.media[key];
            if (item && item.image) {
              mediaImages.push({
                image: item.image,
                name: item.name || ''
              });
            }
          });
        }
      }
      
      // Update ref
      mediaImagesRef.current = mediaImages;
      
      setFormData({
        id: ad.id || '',
        business_name: ad.business_name || '',
        business_email: ad.business_email || '',
        business_phone: ad.business_phone || '',
        business_website: ad.business_website || '',
        business_description: ad.business_description || '',
        description: ad.description || '',
        title: ad.title || '',
        media_images: mediaImages,
        video: ad.video || ad.video_with_subtitles || '',
        video_no_subs: ad.video_no_subs || (ad.video && !ad.video_with_subtitles ? ad.video : ''),
        vimeo: ad.vimeo || ad.youtubeID || '',
        staticQRCODE: ad.staticQRCODE || '',
        titleQRCODE: ad.titleQRCODE || '',
        video_fullscreen: ad.video_fullscreen || '',
        show_fullscreen_video: ad.show_fullscreen_video === true,
        fullscreen_badge_image: ad.fullscreen_badge_image || '',
        paid_until: formatDateForInput(ad.paid_until),
        debug: ad.debug === true,
        enabled: ad.enabled === true || ad.enabled === undefined || ad.enabled === null,
        portrait: ad.portrait === true,
        duration: ad.duration || 20, // Default to 20 if not set
        locations: ad.locations && Array.isArray(ad.locations) ? ad.locations : []
      });
    }
    
    // Cleanup function to revoke blob URLs when component unmounts or ad changes
    return () => {
      if (mediaImagesRef.current) {
        mediaImagesRef.current.forEach(item => {
          if (item && item.image && item.image.startsWith('blob:')) {
            URL.revokeObjectURL(item.image);
          }
        });
      }
    };
  }, [ad]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? (value === '' ? '' : Number(value)) : value)
    }));
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // FFmpeg instance (lazy loaded)
  const ffmpegRef = useRef(null);
  const [ffmpegLoading, setFfmpegLoading] = useState(false);

  // Load FFmpeg on first use
  const loadFFmpeg = async () => {
    if (ffmpegRef.current || ffmpegLoading) return;
    
    setFfmpegLoading(true);
    try {
      console.log('📦 [FRONTEND] Loading FFmpeg.wasm...');
      const ffmpeg = new FFmpeg();
      ffmpegRef.current = ffmpeg;

      // Load FFmpeg core
      const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
      await ffmpeg.load({
        coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
      });

      setFfmpegLoading(false);
      console.log('✅ [FRONTEND] FFmpeg.wasm loaded successfully');
    } catch (error) {
      console.error('❌ [FRONTEND] Failed to load FFmpeg.wasm:', error);
      setFfmpegLoading(false);
      throw error;
    }
  };

  // Function to trim video to 30 seconds on client side using FFmpeg
  const trimVideoTo20Seconds = async (file) => {
    console.log('✂️  [FRONTEND] Trimming video to 30 seconds...');
    console.log(`   Original file size: ${(file.size / 1024 / 1024).toFixed(2)} MB`);
    
    // Check video duration first
    const video = document.createElement('video');
    video.preload = 'metadata';
    const objectUrl = URL.createObjectURL(file);
    video.src = objectUrl;
    
    const duration = await new Promise((resolve, reject) => {
      video.onloadedmetadata = () => {
        resolve(video.duration);
        URL.revokeObjectURL(objectUrl);
      };
      video.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Failed to load video metadata'));
      };
    });
    
    console.log(`   Video duration: ${duration.toFixed(2)} seconds`);
    
    if (duration <= 30) {
      console.log('   Video is already 30 seconds or less, using original file');
      return file;
    }
    
    // Load FFmpeg if not already loaded
    if (!ffmpegRef.current) {
      await loadFFmpeg();
    }
    
    const ffmpeg = ffmpegRef.current;
    
    try {
      // Write input file to FFmpeg
      const inputFileName = 'input.' + file.name.split('.').pop();
      const outputFileName = 'output.mp4';
      
      console.log(`   Writing input file to FFmpeg: ${inputFileName}`);
      await ffmpeg.writeFile(inputFileName, await fetchFile(file));
      
      // Trim video to 30 seconds and compress it aggressively
      console.log(`   Trimming video to 30 seconds and compressing...`);
      
      // Try multiple compression strategies until file is under 4MB
      let outputSize = Infinity;
      let attempts = 0;
      const maxAttempts = 3;
      const targetSize = 4 * 1024 * 1024; // 4MB target (under Vercel's 4.5MB limit)
      
      const compressionPresets = [
        // Preset 1: Aggressive compression
        [
          '-i', inputFileName,
          '-t', '30',
          '-vf', 'scale=1280:-2', // Lower resolution: 1280px max width
          '-c:v', 'libx264',
          '-preset', 'ultrafast', // Fastest encoding
          '-crf', '32', // Higher CRF = smaller file
          '-maxrate', '1M', // Max bitrate: 1 Mbps
          '-bufsize', '2M',
          '-c:a', 'aac',
          '-b:a', '96k', // Lower audio bitrate
          '-movflags', '+faststart',
          '-avoid_negative_ts', 'make_zero',
          outputFileName
        ],
        // Preset 2: Even more aggressive
        [
          '-i', inputFileName,
          '-t', '30',
          '-vf', 'scale=960:-2', // Even lower resolution: 960px max width
          '-c:v', 'libx264',
          '-preset', 'ultrafast',
          '-crf', '35',
          '-maxrate', '800k',
          '-bufsize', '1.6M',
          '-c:a', 'aac',
          '-b:a', '64k',
          '-movflags', '+faststart',
          '-avoid_negative_ts', 'make_zero',
          outputFileName
        ],
        // Preset 3: Maximum compression
        [
          '-i', inputFileName,
          '-t', '30',
          '-vf', 'scale=720:-2', // 720p max
          '-c:v', 'libx264',
          '-preset', 'ultrafast',
          '-crf', '38',
          '-maxrate', '600k',
          '-bufsize', '1.2M',
          '-c:a', 'aac',
          '-b:a', '48k',
          '-movflags', '+faststart',
          '-avoid_negative_ts', 'make_zero',
          outputFileName
        ]
      ];
      
      let finalData = null;
      
      for (let i = 0; i < compressionPresets.length && outputSize > targetSize; i++) {
        attempts++;
        console.log(`   Compression attempt ${attempts}/${maxAttempts}...`);
        
        try {
          await ffmpeg.exec(compressionPresets[i]);
          
          // Check file size
          finalData = await ffmpeg.readFile(outputFileName);
          outputSize = finalData.length;
          
          console.log(`   Compressed size: ${(outputSize / 1024 / 1024).toFixed(2)} MB`);
          
          if (outputSize <= targetSize) {
            console.log(`   ✅ File size is acceptable (under 4MB)`);
            break;
          } else {
            console.log(`   ⚠️  File still too large, trying more aggressive compression...`);
            // Delete output and try next preset
            await ffmpeg.deleteFile(outputFileName);
            finalData = null; // Reset for next attempt
          }
        } catch (error) {
          console.error(`   ❌ Compression attempt ${attempts} failed:`, error);
          if (attempts >= maxAttempts) {
            throw error;
          }
          finalData = null; // Reset for next attempt
        }
      }
      
      if (!finalData) {
        throw new Error('Failed to compress video after all attempts');
      }
      
      if (outputSize > targetSize) {
        console.warn(`   ⚠️  Warning: Final file size (${(outputSize / 1024 / 1024).toFixed(2)} MB) is still above target (4MB)`);
        console.warn(`   This may cause upload issues. Consider using a smaller source video.`);
      }
      
      // Create file from compressed data
      const blob = new Blob([finalData.buffer], { type: 'video/mp4' });
      const trimmedFile = new File([blob], file.name.replace(/\.[^/.]+$/, '.mp4'), { type: 'video/mp4' });
      
      console.log(`   Final trimmed video size: ${(trimmedFile.size / 1024 / 1024).toFixed(2)} MB`);
      console.log(`   Size reduction: ${((1 - trimmedFile.size / file.size) * 100).toFixed(1)}%`);
      
      // Clean up
      await ffmpeg.deleteFile(inputFileName);
      await ffmpeg.deleteFile(outputFileName);
      
      return trimmedFile;
    } catch (error) {
      console.error('❌ [FRONTEND] FFmpeg error:', error);
      throw new Error('Failed to trim video: ' + error.message);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('📤 [FRONTEND] Starting video upload');
    console.log(`   File name: ${file.name}`);
    console.log(`   File size: ${(file.size / 1024 / 1024).toFixed(2)} MB`);
    console.log(`   File type: ${file.type}`);
    console.log(`   API URL: ${API_BASE_URL}`);
    console.log(`   Full endpoint: ${API_BASE_URL}/api/upload-video-ftp`);
    console.log(`   Origin: ${window.location.origin}`);

    // Validate file type
    if (!file.type.startsWith('video/')) {
      alert('אנא בחר קובץ וידאו בלבד');
      return;
    }

    // Validate file size (max 500MB)
    const maxSize = 500 * 1024 * 1024; // 500MB
    if (file.size > maxSize) {
      alert('קובץ הווידאו גדול מדי. מקסימום 500MB');
      return;
    }

    // Check if file is too large for direct upload (Vercel has 4.5MB limit)
    const vercelLimit = 4 * 1024 * 1024; // 4MB to be safe
    const isLargeFile = file.size > vercelLimit;

    setUploadingVideo(true);
    
    try {
      // Trim video to 30 seconds on client side BEFORE uploading
      console.log('✂️  [FRONTEND] Trimming video to 30 seconds before upload...');
      console.log('   This may take a moment for large files...');
      
      const trimmedFile = await trimVideoTo20Seconds(file);
      console.log(`✅ [FRONTEND] Video trimmed successfully`);
      console.log(`   Trimmed file size: ${(trimmedFile.size / 1024 / 1024).toFixed(2)} MB`);
      console.log(`   Is large file (>4MB): ${isLargeFile}`);
      
      let result;
      
      if (isLargeFile) {
        // For large files, upload to Vercel Blob first, then process
        console.log(`📦 [FRONTEND] Large file detected, using Vercel Blob...`);
        
        // Upload to Vercel Blob
        // Note: React only exposes env vars prefixed with REACT_APP_
        // So BLOB_READ_WRITE_TOKEN won't work - must use REACT_APP_BLOB_READ_WRITE_TOKEN
        const blobToken = process.env.REACT_APP_BLOB_READ_WRITE_TOKEN;
        if (!blobToken) {
          throw new Error('REACT_APP_BLOB_READ_WRITE_TOKEN not configured. Please add REACT_APP_BLOB_READ_WRITE_TOKEN environment variable in Vercel (frontend project) with the same value as BLOB_READ_WRITE_TOKEN.');
        }
        
        const sanitizedAdName = (ad?.id || 'video').replace(/[^a-zA-Z0-9_-]/g, '_');
        const blobFilename = `${sanitizedAdName}-${Date.now()}.mp4`;
        
        console.log(`📤 [FRONTEND] Uploading to Vercel Blob...`);
        console.log(`   Filename: ${blobFilename}`);
        const blobResult = await put(blobFilename, trimmedFile, {
          access: 'public',
          token: blobToken,
          contentType: trimmedFile.type || 'video/mp4'
        });
        
        console.log(`✅ [FRONTEND] Uploaded to Blob: ${blobResult.url}`);
        
        // Now send Blob URL to process-video endpoint
        console.log(`🔄 [FRONTEND] Sending Blob URL to process-video endpoint...`);
        const processResponse = await fetch(`${API_BASE_URL}/api/process-video`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            videoUrl: blobResult.url
          })
        });
        
        if (!processResponse.ok) {
          const errorText = await processResponse.text();
          let errorResult;
          try {
            errorResult = JSON.parse(errorText);
          } catch (e) {
            errorResult = { error: errorText };
          }
          throw new Error(errorResult.error || `HTTP ${processResponse.status}: ${processResponse.statusText}`);
        }
        
        result = await processResponse.json();
        console.log(`✅ [FRONTEND] Video processed successfully`);
        console.log(`   Result:`, result);
      } else {
        // For small files, use direct upload
        const formData = new FormData();
        formData.append('video', trimmedFile);
        formData.append('adName', ad?.id || 'unknown');

        console.log(`📡 [FRONTEND] Sending POST request to: ${API_BASE_URL}/api/upload-video-ftp`);
        console.log(`   Ad name: ${ad?.id || 'unknown'}`);
        console.log(`   Request headers will be set by browser for FormData`);

        const startTime = Date.now();
        const response = await fetch(`${API_BASE_URL}/api/upload-video-ftp`, {
          method: 'POST',
          body: formData,
          // Don't set Content-Type header - browser will set it with boundary for FormData
        });

        const duration = Date.now() - startTime;
        console.log(`📥 [FRONTEND] Received response after ${duration}ms`);
        console.log(`   Status: ${response.status} ${response.statusText}`);
        console.log(`   Headers:`, Object.fromEntries(response.headers.entries()));
        console.log(`   OK: ${response.ok}`);

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`❌ [FRONTEND] Response not OK`);
          console.error(`   Status: ${response.status}`);
          console.error(`   Error text: ${errorText.substring(0, 500)}`);
          
          let errorResult;
          try {
            errorResult = JSON.parse(errorText);
          } catch (e) {
            errorResult = { error: errorText };
          }
          
          throw new Error(errorResult.error || `HTTP ${response.status}: ${response.statusText}`);
        }

        result = await response.json();
        console.log(`✅ [FRONTEND] Response parsed successfully`);
        console.log(`   Result:`, result);
      }

      if (result.success && result.url) {
        // Update both video and video_no_subs with the FTP URL (new upload)
        setFormData(prev => ({
          ...prev,
          video: result.url,
          video_no_subs: result.url
        }));
        console.log(`✅ [FRONTEND] Video URL updated in form: ${result.url}`);
        console.log(`   Both video and video_no_subs set to: ${result.url}`);
      } else {
        throw new Error('לא התקבל קישור לווידאו');
      }
    } catch (error) {
      console.error('❌ [FRONTEND] Error uploading video:', error);
      console.error(`   Error name: ${error.name}`);
      console.error(`   Error message: ${error.message}`);
      console.error(`   Error stack:`, error.stack);
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      alert(`שגיאה בהעלאת הווידאו: ${error.message}`);
    } finally {
      setUploadingVideo(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Helper function to format phone number
  const formatPhone = (phone) => {
    if (!phone) return null;
    // Remove any non-digit characters
    const digits = phone.replace(/\D/g, '');
    // Add country code if not present
    if (!digits.startsWith('972')) {
      return '972' + digits.replace(/^0+/, '');
    }
    return digits;
  };

  // Helper function to create Linkly URL (via backend to avoid CORS)
  const createLinklyUrl = async (targetUrl, name, adId, title, customName = null) => {
    if (!targetUrl) return null;
    
    // Use custom name if provided, otherwise use default format
    const linklyName = customName || (name && adId && title 
      ? `${name}-${adId} - ${title}` 
      : null);
    
    try {
      console.log(`📱 Calling backend API to create Linkly URL...`);
      console.log(`   Target URL: ${targetUrl}`);
      console.log(`   Link name: ${linklyName || 'Not provided'}`);
      
      const response = await fetch(`${API_BASE_URL}/api/create-linkly-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          targetUrl,
          linklyName
        })
      });

      if (response.ok) {
        const data = await response.json();
        const fullUrl = data.full_url || targetUrl;
        console.log(`✅ Linkly URL created: ${fullUrl}`);
        return fullUrl;
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Error creating Linkly URL:', errorData.error || 'Unknown error');
        return targetUrl;
      }
    } catch (error) {
      console.error('Error calling Linkly API:', error);
      return targetUrl;
    }
  };

  // Handle creating Linkly link for WhatsApp
  const handleCreateLinkly = async () => {
    // Get phone number from form data
    const whatsappPhone = formData.business_phone;
    
    if (!whatsappPhone || !whatsappPhone.trim()) {
      alert('אין מספר טלפון. אנא הזן מספר טלפון בשדה "מספר טלפון" לפני יצירת קישור Linkly.');
      return;
    }

    const formattedPhone = formatPhone(whatsappPhone);
    if (!formattedPhone) {
      alert('מספר הטלפון לא תקין. אנא בדוק את מספר הטלפון.');
      return;
    }

    setCreatingLinkly(true);
    
    try {
      // Fixed WhatsApp message text: "הגעתי דרך GSRadio, אשמח לפרטים נוספים"
      const whatsappMessage = encodeURIComponent('הגעתי דרך GSRadio, אשמח לפרטים נוספים');
      const whatsappUrl = `https://wa.me/${formattedPhone}?text=${whatsappMessage}`;
      
      // Build link name: business_name-ad_id-business_description
      const businessName = formData.business_name || formData.title || '';
      const businessDescription = formData.business_description || '';
      const adId = formData.id || ad?.id || 'unknown';
      const linklyName = `${businessName}-${adId}-${businessDescription}`;
      
      console.log(`📱 Creating WhatsApp Linkly link:`);
      console.log(`   WhatsApp URL: ${whatsappUrl}`);
      console.log(`   Phone: ${formattedPhone}`);
      console.log(`   Link name: ${linklyName}`);
      
      // Create Linkly link from WhatsApp URL
      const linklyUrl = await createLinklyUrl(whatsappUrl, businessName, adId, businessDescription, linklyName);
      
      if (linklyUrl) {
        console.log(`✅ Linkly link created: ${linklyUrl}`);
        // Update the staticQRCODE field with the Linkly URL
        setFormData(prev => ({
          ...prev,
          staticQRCODE: linklyUrl
        }));
        alert('קישור Linkly נוצר בהצלחה!');
      } else {
        console.error(`❌ Failed to create Linkly link`);
        alert('שגיאה ביצירת קישור Linkly. אנא נסה שוב.');
      }
    } catch (error) {
      console.error('Error creating Linkly link:', error);
      alert(`שגיאה ביצירת קישור Linkly: ${error.message}`);
    } finally {
      setCreatingLinkly(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('יש לבחור קובץ תמונה');
      return;
    }

    // Create image URL for preview
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setShowImageCrop(true);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setEditingImageIndex(null); // null means adding new image
    };
    reader.readAsDataURL(file);
  };

  const handleBadgeImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('יש לבחור קובץ תמונה');
      return;
    }

    setUploadingBadgeImage(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('image', file);
      formDataToSend.append('adName', (ad?.id || 'unknown') + '-fullscreen-badge');
      const response = await fetch(`${API_BASE_URL}/api/upload-image-ftp`, {
        method: 'POST',
        body: formDataToSend,
      });
      if (!response.ok) {
        throw new Error(`שגיאה בהעלאת התמונה: ${response.status}`);
      }
      const result = await response.json();
      if (result.success && result.url) {
        setFormData(prev => ({ ...prev, fullscreen_badge_image: result.url }));
      } else {
        throw new Error('לא התקבל קישור לתמונה');
      }
    } catch (error) {
      console.error('Error uploading badge image:', error);
      alert(`שגיאה בהעלאת תג מסך מלא: ${error.message}`);
    } finally {
      setUploadingBadgeImage(false);
      if (badgeImageInputRef.current) {
        badgeImageInputRef.current.value = '';
      }
    }
  };

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.setAttribute('crossOrigin', 'anonymous');
      image.src = url;
    });

  const getCroppedImg = async (imageSrc, pixelCrop, options = {}) => {
    const outWidth = options.width ?? 500;
    const outHeight = options.height ?? 500;
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = outWidth;
    canvas.height = outHeight;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      outWidth,
      outHeight
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.9);
    });
  };

  const handleCropComplete = async () => {
    if (!imageSrc || !croppedAreaPixels) {
      alert('יש לבחור אזור חיתוך');
      return;
    }

    // Close crop modal immediately
    setShowImageCrop(false);

    // Create cropped image blob
    const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
    
    // Create blob URL for immediate preview
    const blobUrl = URL.createObjectURL(croppedImage);
    
    // Add image to array immediately with blob URL
    if (editingImageIndex !== null) {
      // Update existing image
      setFormData(prev => {
        const newMediaImages = [...prev.media_images];
        // Revoke old blob URL if exists
        if (newMediaImages[editingImageIndex] && newMediaImages[editingImageIndex].image && newMediaImages[editingImageIndex].image.startsWith('blob:')) {
          URL.revokeObjectURL(newMediaImages[editingImageIndex].image);
        }
        newMediaImages[editingImageIndex] = {
          ...newMediaImages[editingImageIndex],
          image: blobUrl,
          isUploading: true // Flag to show upload status
        };
        mediaImagesRef.current = newMediaImages;
        return {
          ...prev,
          media_images: newMediaImages
        };
      });
    } else {
      // Add new image
      setFormData(prev => {
        const newMediaImages = [...prev.media_images, { image: blobUrl, name: '', isUploading: true }];
        mediaImagesRef.current = newMediaImages;
        return {
          ...prev,
          media_images: newMediaImages
        };
      });
    }
    
    // Clean up crop state
    setImageSrc(null);
    setEditingImageIndex(null);
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }

    // Upload to server in background
    setUploadingImage(true);
    
    try {
      const file = new File([croppedImage], 'cropped-image.jpg', { type: 'image/jpeg' });

      console.log('📸 [FRONTEND] Starting cropped image upload');
      console.log(`   File name: ${file.name}`);
      console.log(`   File size: ${(file.size / 1024).toFixed(2)} KB`);
      console.log(`   File type: ${file.type}`);
      console.log(`   API URL: ${API_BASE_URL}`);
      console.log(`   Full endpoint: ${API_BASE_URL}/api/upload-image-ftp`);

      const formData = new FormData();
      formData.append('image', file);
      formData.append('adName', ad?.id || 'unknown');

      console.log(`📡 [FRONTEND] Sending POST request to: ${API_BASE_URL}/api/upload-image-ftp`);
      console.log(`   Ad name: ${ad?.id || 'unknown'}`);

      const response = await fetch(`${API_BASE_URL}/api/upload-image-ftp`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ [FRONTEND] Response not OK`);
        console.error(`   Status: ${response.status}`);
        console.error(`   Error text: ${errorText}`);
        throw new Error(`שגיאה בהעלאת התמונה: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log(`✅ [FRONTEND] Image upload successful`);
      console.log(`   Result:`, result);

      if (result.success && result.url) {
        // Update the image URL from blob URL to server URL
        setFormData(prev => {
          const newMediaImages = [...prev.media_images];
          const targetIndex = editingImageIndex !== null ? editingImageIndex : newMediaImages.length - 1;
          
          // Revoke the old blob URL to free memory
          const oldImage = newMediaImages[targetIndex];
          if (oldImage && oldImage.image && oldImage.image.startsWith('blob:')) {
            URL.revokeObjectURL(oldImage.image);
          }
          
          newMediaImages[targetIndex] = {
            ...newMediaImages[targetIndex],
            image: result.url,
            isUploading: false
          };
          
          mediaImagesRef.current = newMediaImages;
          
          return {
            ...prev,
            media_images: newMediaImages
          };
        });
        
        if (editingImageIndex !== null) {
          console.log(`✅ [FRONTEND] Image ${editingImageIndex} updated in form: ${result.url}`);
        } else {
          console.log(`✅ [FRONTEND] New image added to form: ${result.url}`);
        }
      } else {
        throw new Error('לא התקבל קישור לתמונה');
      }
    } catch (error) {
      console.error('❌ [FRONTEND] Error uploading image:', error);
      
      // Remove the image from array if upload failed
      setFormData(prev => {
        const newMediaImages = [...prev.media_images];
        const targetIndex = editingImageIndex !== null ? editingImageIndex : newMediaImages.length - 1;
        
        // Revoke blob URL before removing
        const oldImage = newMediaImages[targetIndex];
        if (oldImage && oldImage.image && oldImage.image.startsWith('blob:')) {
          URL.revokeObjectURL(oldImage.image);
        }
        
        if (editingImageIndex !== null) {
          // Keep the image but mark as error
          newMediaImages[targetIndex] = {
            ...newMediaImages[targetIndex],
            isUploading: false,
            uploadError: true
          };
        } else {
          // Remove new image if upload failed
          newMediaImages.splice(targetIndex, 1);
        }
        
        return {
          ...prev,
          media_images: newMediaImages
        };
      });
      
      alert(`שגיאה בהעלאת התמונה: ${error.message}`);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCropCancel = () => {
    setShowImageCrop(false);
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setEditingImageIndex(null);
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
    if (badgeImageInputRef.current) {
      badgeImageInputRef.current.value = '';
    }
  };

  const handleEditImage = (index) => {
    const imageItem = formData.media_images[index];
    if (!imageItem || !imageItem.image) return;
    
    // Load existing image for editing
    setImageSrc(imageItem.image);
    setShowImageCrop(true);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setEditingImageIndex(index);
  };

  const handleDeleteImage = (index) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק תמונה זו?')) {
      setFormData(prev => {
        const newMediaImages = [...prev.media_images];
        const imageToDelete = newMediaImages[index];
        
        // Revoke blob URL if it's a blob URL
        if (imageToDelete && imageToDelete.image && imageToDelete.image.startsWith('blob:')) {
          URL.revokeObjectURL(imageToDelete.image);
        }
        
        newMediaImages.splice(index, 1);
        mediaImagesRef.current = newMediaImages;
        
        return {
          ...prev,
          media_images: newMediaImages
        };
      });
    }
  };

  const handleImageNameChange = (index, name) => {
    setFormData(prev => {
      const newMediaImages = [...prev.media_images];
      newMediaImages[index] = {
        ...newMediaImages[index],
        name: name
      };
      return {
        ...prev,
        media_images: newMediaImages
      };
    });
  };

  const handleVoiceToText = async () => {
    // Use original video (video_no_subs) as source
    const videoUrl = formData.video_no_subs;
    
    if (!videoUrl || videoUrl.trim() === '') {
      alert('יש להזין כתובת וידאו מקורי לפני המרת קול לכתוביות');
      return;
    }

    setConvertingVoiceToText(true);
    
    try {
      console.log('🎤 [FRONTEND] Starting voice to text conversion...');
      console.log(`   Video URL: ${videoUrl}`);
      
      const response = await fetch(`${API_BASE_URL}/api/add-subtitles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          videoUrl: videoUrl,
          adName: ad?.id || 'unknown',
          subtitleType: 'voice-to-text' // Specify subtitle type
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'שגיאה בהמרת קול לכתוביות');
      }

      if (result.success && result.url) {
        // Update only the video field (with subtitles)
        setFormData(prev => ({
          ...prev,
          video: result.url
        }));
        console.log(`✅ [FRONTEND] Video with subtitles URL updated in form: ${result.url}`);
        console.log(`   Original video URL preserved: ${formData.video_no_subs}`);
        alert('הקול הומר לכתוביות בהצלחה! הווידאו עם כתוביות עודכן בשדה המתאים.');
      } else {
        throw new Error('לא התקבל קישור לווידאו עם כתוביות');
      }
    } catch (error) {
      console.error('Error converting voice to text:', error);
      alert(`שגיאה בהמרת קול לכתוביות: ${error.message}`);
    } finally {
      setConvertingVoiceToText(false);
    }
  };

  const handleGenerateAISubtitles = async () => {
    // Use original video (video_no_subs) as source
    const videoUrl = formData.video_no_subs;
    
    if (!videoUrl || videoUrl.trim() === '') {
      alert('יש להזין כתובת וידאו מקורי לפני יצירת כתוביות AI');
      return;
    }

    // Check if we have business information for GPT
    const hasBusinessInfo = formData.business_description || formData.description || formData.title;
    
    if (!hasBusinessInfo || hasBusinessInfo.trim() === '') {
      alert('יש להזין תיאור עסק, תיאור או כותרת כדי ליצור כתוביות AI');
      return;
    }

    setGeneratingAISubtitles(true);
    
    try {
      console.log('🤖 [FRONTEND] Starting AI subtitle generation...');
      console.log(`   Video URL: ${videoUrl}`);
      console.log(`   Business description: ${formData.business_description ? 'provided' : 'not provided'}`);
      console.log(`   Description: ${formData.description ? 'provided' : 'not provided'}`);
      console.log(`   Title: ${formData.title ? 'provided' : 'not provided'}`);
      
      const response = await fetch(`${API_BASE_URL}/api/add-subtitles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          videoUrl: videoUrl,
          adName: ad?.id || 'unknown',
          subtitleType: 'ai-generated', // Specify subtitle type
          businessDescription: formData.business_description || '',
          description: formData.description || '',
          title: formData.title || ''
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'שגיאה ביצירת כתוביות AI');
      }

      if (result.success && result.url) {
        // Update the video_with_subtitles field with the new URL
        setFormData(prev => ({
          ...prev,
          video: result.url
        }));
        console.log(`✅ [FRONTEND] Video with AI subtitles URL updated in form: ${result.url}`);
        console.log(`   Original video URL preserved: ${formData.video_no_subs}`);
      } else {
        throw new Error('לא התקבל קישור לווידאו עם כתוביות');
      }
    } catch (error) {
      console.error('Error generating AI subtitles:', error);
      alert(`שגיאה ביצירת כתוביות AI: ${error.message}`);
    } finally {
      setGeneratingAISubtitles(false);
    }
  };

  const validateUrl = (url) => {
    if (!url || url.trim() === '') return true; // Empty URLs are allowed
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validate ad ID for new ads
    if (isNewAd) {
      if (!formData.id || formData.id.trim() === '') {
        newErrors.id = 'יש להזין מזהה פרסומת';
      } else if (!/^[a-zA-Z0-9_-]+$/.test(formData.id)) {
        newErrors.id = 'מזהה הפרסומת יכול להכיל רק אותיות, מספרים, מקפים ותחתונים';
      }
    }
    
    // Validate URLs if provided
    formData.media_images.forEach((item, index) => {
      if (item.image && !validateUrl(item.image)) {
        newErrors[`media_image_${index}`] = 'כתובת תמונה לא תקינה';
      }
    });
    if (formData.video && !validateUrl(formData.video)) {
      newErrors.video = 'כתובת וידאו עם כתוביות לא תקינה';
    }
    if (formData.video_no_subs && !validateUrl(formData.video_no_subs)) {
      newErrors.video_no_subs = 'כתובת וידאו מקורי לא תקינה';
    }
    if (formData.staticQRCODE && !validateUrl(formData.staticQRCODE)) {
      newErrors.staticQRCODE = 'כתובת QR code לא תקינה';
    }
    if (formData.business_website && !validateUrl(formData.business_website)) {
      newErrors.business_website = 'כתובת אתר לא תקינה';
    }
    if (formData.fullscreen_badge_image && !validateUrl(formData.fullscreen_badge_image)) {
      newErrors.fullscreen_badge_image = 'כתובת תג מסך מלא לא תקינה';
    }
    
    // Validate email if provided
    if (formData.business_email && formData.business_email.trim() !== '') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.business_email)) {
        newErrors.business_email = 'כתובת אימייל לא תקינה';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Build updated ad data
      const updatedAdData = {
        // Include ID for new ads
        ...(isNewAd && formData.id ? { id: formData.id.trim() } : {}),
        business_name: formData.business_name || '',
        business_email: formData.business_email || '',
        business_phone: formData.business_phone || '',
        business_website: formData.business_website || '',
        business_description: formData.business_description || '',
        description: formData.description || '',
        title: formData.title || '',
        video: formData.video || '',
        video_no_subs: formData.video_no_subs || '',
        vimeo: formData.vimeo || '',
        staticQRCODE: formData.staticQRCODE || '',
        titleQRCODE: formData.titleQRCODE || '',
        video_fullscreen: formData.video_fullscreen || '',
        show_fullscreen_video: formData.show_fullscreen_video,
        fullscreen_badge_image: formData.fullscreen_badge_image || '',
        paid_until: formData.paid_until || '',
        debug: formData.debug,
        enabled: formData.enabled,
        portrait: formData.portrait,
        duration: formData.duration || 20, // Default to 20 if empty
        locations: formData.locations && Array.isArray(formData.locations) && formData.locations.length > 0 
          ? formData.locations 
          : []
      };
      
      // Build media object in the format expected by Firebase
      // Convert array to object with numeric keys
      if (formData.media_images && formData.media_images.length > 0) {
        const mediaObject = {};
        formData.media_images.forEach((item, index) => {
          if (item && item.image && item.image.trim() !== '') {
            mediaObject[index] = {
              image: item.image.trim(),
              name: (item.name || '').trim()
            };
          }
        });
        updatedAdData.media = mediaObject;
      } else {
        // If no images, set media to empty object to remove it from Firebase
        updatedAdData.media = {};
      }
      
      await onSave(updatedAdData);
    } catch (error) {
      console.error('Error saving ad:', error);
      alert('שגיאה בשמירת הפרסומת. נסה שוב.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!ad) {
    return (
      <div className="edit-ad-form-empty">
        <p>לא נבחרה פרסומת לעריכה</p>
      </div>
    );
  }

  return (
    <div className="edit-ad-form">
      <div className="edit-ad-form-header">
        <h2>{isNewAd ? 'יצור פרסומת חדשה' : `ערוך פרסומת: ${ad.id}`}</h2>
        <button
          className="edit-ad-form-cancel"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          ביטול
        </button>
      </div>
      
      <form onSubmit={handleSubmit} className="edit-ad-form-content">
        {/* Ad ID Section - Only for new ads */}
        {isNewAd && (
          <div className="edit-ad-form-section">
            <h3>מזהה פרסומת</h3>
            <div className="edit-ad-form-grid">
              <div className="edit-ad-form-field">
                <label htmlFor="id">מזהה פרסומת *</label>
                <input
                  type="text"
                  id="id"
                  name="id"
                  value={formData.id}
                  onChange={handleChange}
                  placeholder="לדוגמה: ad-001"
                />
                {errors.id && (
                  <span className="edit-ad-form-error">{errors.id}</span>
                )}
                <small className="edit-ad-form-hint">
                  מזהה ייחודי לפרסומת (אותיות, מספרים, מקפים ותחתונים בלבד)
                </small>
              </div>
            </div>
          </div>
        )}
        
        {/* Business Information Section */}
        <div className="edit-ad-form-section">
          <h3>מידע עסקי</h3>
          <div className="edit-ad-form-grid">
            <div className="edit-ad-form-field">
              <label htmlFor="business_name">שם העסק</label>
              <input
                type="text"
                id="business_name"
                name="business_name"
                value={formData.business_name}
                onChange={handleChange}
              />
            </div>
            
            <div className="edit-ad-form-field">
              <label htmlFor="business_email">אימייל</label>
              <input
                type="email"
                id="business_email"
                name="business_email"
                value={formData.business_email}
                onChange={handleChange}
              />
              {errors.business_email && (
                <span className="edit-ad-form-error">{errors.business_email}</span>
              )}
            </div>
            
            <div className="edit-ad-form-field">
              <label htmlFor="business_phone">טלפון</label>
              <input
                type="tel"
                id="business_phone"
                name="business_phone"
                value={formData.business_phone}
                onChange={handleChange}
              />
            </div>
            
            <div className="edit-ad-form-field">
              <label htmlFor="business_website">אתר אינטרנט</label>
              <input
                type="url"
                id="business_website"
                name="business_website"
                value={formData.business_website}
                onChange={handleChange}
                placeholder="https://example.com"
              />
              {errors.business_website && (
                <span className="edit-ad-form-error">{errors.business_website}</span>
              )}
            </div>
            
            <div className="edit-ad-form-field edit-ad-form-field-full">
              <label htmlFor="business_description">תיאור העסק</label>
              <textarea
                id="business_description"
                name="business_description"
                value={formData.business_description}
                onChange={handleChange}
                rows={3}
              />
            </div>
          </div>
        </div>
        
        {/* Visual Fields Section */}
        <div className="edit-ad-form-section">
          <h3>שדות ויזואליים</h3>
          <div className="edit-ad-form-grid">
            <div className="edit-ad-form-field edit-ad-form-field-full">
              <label htmlFor="description">תיאור (באנר עליון)</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
              />
            </div>
            
            <div className="edit-ad-form-field">
              <label htmlFor="title">כותרת</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
              />
            </div>
            
            <div className="edit-ad-form-field edit-ad-form-field-full">
              <label>תמונות</label>
              <div className="edit-ad-form-images-container">
                {formData.media_images.map((item, index) => (
                  <div key={index} className="edit-ad-form-image-item">
                    <div className="edit-ad-form-image-wrapper">
                      <img 
                        src={item.image} 
                        alt={item.name || `תמונה ${index + 1}`}
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23ddd" width="200" height="200"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="14" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3Eתמונה לא זמינה%3C/text%3E%3C/svg%3E';
                        }}
                      />
                      {item.isUploading && (
                        <div className="edit-ad-form-image-uploading">
                          <div className="edit-ad-form-image-uploading-spinner"></div>
                          <span>מעלה...</span>
                        </div>
                      )}
                      {item.uploadError && (
                        <div className="edit-ad-form-image-error">
                          <span>❌ שגיאה בהעלאה</span>
                        </div>
                      )}
                      <div className="edit-ad-form-image-actions">
                        <button
                          type="button"
                          className="edit-ad-form-image-edit-button"
                          onClick={() => handleEditImage(index)}
                          disabled={uploadingImage || showImageCrop || item.isUploading}
                          title="ערוך תמונה"
                        >
                          ✏️
                        </button>
                        <button
                          type="button"
                          className="edit-ad-form-image-delete-button"
                          onClick={() => handleDeleteImage(index)}
                          disabled={uploadingImage || showImageCrop || item.isUploading}
                          title="מחק תמונה"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                    <input
                      type="text"
                      className="edit-ad-form-image-title"
                      value={item.name}
                      onChange={(e) => handleImageNameChange(index, e.target.value)}
                      placeholder="כותרת תמונה"
                      disabled={item.isUploading}
                    />
                  </div>
                ))}
                <div className="edit-ad-form-image-item edit-ad-form-image-add">
                  <button
                    type="button"
                    className="edit-ad-form-image-add-button"
                    onClick={() => imageInputRef.current?.click()}
                    disabled={uploadingImage || showImageCrop}
                  >
                    <span className="edit-ad-form-image-add-icon">+</span>
                    <span className="edit-ad-form-image-add-text">הוסף תמונה</span>
                  </button>
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleImageSelect}
                  />
                </div>
              </div>
            </div>

            {/* Image Crop Modal */}
            {showImageCrop && imageSrc && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                zIndex: 10000,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px'
              }}>
                <div style={{
                  width: '100%',
                  maxWidth: '600px',
                  height: '500px',
                  position: 'relative',
                  backgroundColor: '#000',
                  marginBottom: '20px'
                }}>
                  <Cropper
                    image={imageSrc}
                    crop={crop}
                    zoom={zoom}
                    aspect={1}
                    minZoom={0.1}
                    maxZoom={3}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                    cropShape="rect"
                    showGrid={true}
                    restrictPosition={false}
                  />
                </div>
                <div style={{ marginBottom: '20px', width: '100%', maxWidth: '600px' }}>
                  <label style={{ color: 'white', display: 'block', marginBottom: '10px' }}>
                    זום: {Math.round(zoom * 100)}%
                  </label>
                  <input
                    type="range"
                    min={0.1}
                    max={3}
                    step={0.05}
                    value={zoom}
                    onChange={(e) => setZoom(parseFloat(e.target.value))}
                    style={{ width: '100%' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    type="button"
                    onClick={handleCropCancel}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#666',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    ביטול
                  </button>
                  <button
                    type="button"
                    onClick={handleCropComplete}
                    disabled={uploadingImage || uploadingBadgeImage}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: uploadingImage || uploadingBadgeImage ? 'not-allowed' : 'pointer',
                      opacity: uploadingImage || uploadingBadgeImage ? 0.6 : 1
                    }}
                  >
                    {uploadingImage || uploadingBadgeImage ? 'מעלה...' : 'אישור והעלאה'}
                  </button>
                </div>
              </div>
            )}
            
            <div className="edit-ad-form-field">
              <label htmlFor="vimeo">YouTube/Vimeo ID או Google Drive Link</label>
              <input
                type="text"
                id="vimeo"
                name="vimeo"
                value={formData.vimeo}
                onChange={handleChange}
                placeholder="YouTube ID, Vimeo ID, or Google Drive URL"
              />
            </div>
            
            <div className="edit-ad-form-field">
              <label htmlFor="video_no_subs">כתובת וידאו ישירה (MP4) של וידאו מקורי</label>
              <div className="edit-ad-form-video-upload">
                <input
                  type="url"
                  id="video_no_subs"
                  name="video_no_subs"
                  value={formData.video_no_subs}
                  onChange={handleChange}
                  placeholder="https://example.com/video.mp4"
                />
                <div className="edit-ad-form-video-buttons">
                  <button
                    type="button"
                    className="edit-ad-form-upload-button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingVideo}
                  >
                    {uploadingVideo ? 'מעלה...' : 'העלה וידאו'}
                  </button>
                  <button
                    type="button"
                    className="edit-ad-form-voice-to-text-button"
                    onClick={handleVoiceToText}
                    disabled={convertingVoiceToText || generatingAISubtitles || !formData.video_no_subs}
                  >
                    {convertingVoiceToText ? 'ממיר קול לכתוביות...' : 'הפוך קול לכתוביות'}
                  </button>
                  <button
                    type="button"
                    className="edit-ad-form-ai-subtitles-button"
                    onClick={handleGenerateAISubtitles}
                    disabled={generatingAISubtitles || convertingVoiceToText || !formData.video_no_subs}
                  >
                    {generatingAISubtitles ? 'יוצר כתוביות AI...' : 'צור כתוביות AI'}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="video/*"
                    style={{ display: 'none' }}
                    onChange={handleVideoUpload}
                  />
                </div>
              </div>
              {errors.video_no_subs && (
                <span className="edit-ad-form-error">{errors.video_no_subs}</span>
              )}
            </div>
            
            <div className="edit-ad-form-field">
              <label htmlFor="video">כתובת וידאו ישירה (MP4) עם כתוביות</label>
              <input
                type="url"
                id="video"
                name="video"
                value={formData.video}
                onChange={handleChange}
                placeholder="https://example.com/video-with-subtitles.mp4"
              />
              {errors.video && (
                <span className="edit-ad-form-error">{errors.video}</span>
              )}
              <small className="edit-ad-form-hint">
                שדה זה מתעדכן אוטומטית לאחר יצירת כתוביות באמצעות הכפתורים למעלה
              </small>
            </div>
            
            <div className="edit-ad-form-field">
              <label htmlFor="video_fullscreen">כתובת וידאו מסך מלא (MP4)</label>
              <input
                type="url"
                id="video_fullscreen"
                name="video_fullscreen"
                value={formData.video_fullscreen}
                onChange={handleChange}
                placeholder="https://example.com/video-fullscreen.mp4"
              />
              {errors.video_fullscreen && (
                <span className="edit-ad-form-error">{errors.video_fullscreen}</span>
              )}
              <small className="edit-ad-form-hint">
                וידאו להצגה במסך מלא. אם ריק, ישתמש בשדה וידאו רגיל
              </small>
            </div>
            
            <div className="edit-ad-form-field">
              <label htmlFor="fullscreen_badge_image">תג מסך מלא (FullScreen Badge Image)</label>
              <div className="edit-ad-form-badge-row">
                <input
                  type="url"
                  id="fullscreen_badge_image"
                  name="fullscreen_badge_image"
                  value={formData.fullscreen_badge_image}
                  onChange={handleChange}
                  placeholder="https://example.com/badge.png"
                  className="edit-ad-form-badge-url"
                />
                <button
                  type="button"
                  className="edit-ad-form-upload-button edit-ad-form-badge-upload"
                  onClick={() => badgeImageInputRef.current?.click()}
                  disabled={uploadingBadgeImage || showImageCrop}
                >
                  {uploadingBadgeImage ? 'מעלה...' : 'העלה תמונה'}
                </button>
                <input
                  ref={badgeImageInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleBadgeImageSelect}
                />
              </div>
              {formData.fullscreen_badge_image && (
                <div className="edit-ad-form-badge-preview">
                  <img
                    src={formData.fullscreen_badge_image}
                    alt="תג מסך מלא"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="120" height="60"%3E%3Crect fill="%23ddd" width="120" height="60"/%3E%3Ctext fill="%23999" font-size="12" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3Eלא זמין%3C/text%3E%3C/svg%3E';
                    }}
                  />
                </div>
              )}
              {errors.fullscreen_badge_image && (
                <span className="edit-ad-form-error">{errors.fullscreen_badge_image}</span>
              )}
              <small className="edit-ad-form-hint">
                תמונה שמוצגת מעל וידאו מסך מלא (למשל לוגו או תג). אפשר להזין כתובת או להעלות תמונה – התמונה תישמר כמו שהיא (ללא חיתוך וללא שינוי).
              </small>
            </div>
            
            <div className="edit-ad-form-field">
              <label htmlFor="staticQRCODE">כתובת QR Code</label>
              <input
                type="url"
                id="staticQRCODE"
                name="staticQRCODE"
                value={formData.staticQRCODE}
                onChange={handleChange}
                placeholder="https://example.com/qr"
              />
              {errors.staticQRCODE && (
                <span className="edit-ad-form-error">{errors.staticQRCODE}</span>
              )}
              <button
                type="button"
                onClick={handleCreateLinkly}
                disabled={creatingLinkly}
                className="edit-ad-form-button edit-ad-form-button-secondary"
                style={{ marginTop: '8px', width: '100%' }}
              >
                {creatingLinkly ? 'יוצר Linkly...' : 'צור Linkly'}
              </button>
            </div>
            
            <div className="edit-ad-form-field">
              <label htmlFor="titleQRCODE">כותרת QR Code</label>
              <input
                type="text"
                id="titleQRCODE"
                name="titleQRCODE"
                value={formData.titleQRCODE}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>
        
        {/* Locations Section */}
        <div className="edit-ad-form-section">
          <h3>מיקומי הפרסומת</h3>
          <div className="edit-ad-form-grid">
            <div className="edit-ad-form-field edit-ad-form-field-full">
              <LocationPicker
                locations={formData.locations || []}
                onChange={(newLocations) => {
                  setFormData(prev => ({
                    ...prev,
                    locations: newLocations
                  }));
                }}
              />
            </div>
          </div>
        </div>
        
        {/* Settings Section */}
        <div className="edit-ad-form-section">
          <h3>הגדרות</h3>
          <div className="edit-ad-form-grid">
            <div className="edit-ad-form-field">
              <label htmlFor="paid_until">תאריך סיום מנוי</label>
              <input
                type="date"
                id="paid_until"
                name="paid_until"
                value={formData.paid_until}
                onChange={handleChange}
              />
            </div>
            
            <div className="edit-ad-form-field">
              <label htmlFor="duration">זמן הפרסומת (שניות)</label>
              <input
                type="number"
                id="duration"
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                min="1"
                placeholder="20"
              />
              <small className="edit-ad-form-hint">
                ברירת מחדל: 30 שניות
              </small>
            </div>
            
            <div className="edit-ad-form-field edit-ad-form-checkbox">
              <label>
                <input
                  type="checkbox"
                  name="debug"
                  checked={formData.debug}
                  onChange={handleChange}
                />
                <span>מצב בדיקה (Testing)</span>
              </label>
            </div>
            
            <div className="edit-ad-form-field edit-ad-form-checkbox">
              <label>
                <input
                  type="checkbox"
                  name="enabled"
                  checked={formData.enabled}
                  onChange={handleChange}
                />
                <span>פרסומת פעילה (Live)</span>
              </label>
            </div>
            
            <div className="edit-ad-form-field edit-ad-form-checkbox">
              <label>
                <input
                  type="checkbox"
                  name="portrait"
                  checked={formData.portrait}
                  onChange={handleChange}
                />
                <span>פורטרט (Portrait)</span>
              </label>
            </div>
            
            <div className="edit-ad-form-field edit-ad-form-checkbox">
              <label>
                <input
                  type="checkbox"
                  name="show_fullscreen_video"
                  checked={formData.show_fullscreen_video}
                  onChange={handleChange}
                />
                <span>הצג וידאו מסך מלא</span>
              </label>
            </div>
          </div>
        </div>
        
        {/* Form Actions */}
        <div className="edit-ad-form-actions">
          <button
            type="button"
            className="edit-ad-form-cancel-button"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            ביטול
          </button>
          <button
            type="submit"
            className="edit-ad-form-save-button"
            disabled={isSubmitting || loading}
          >
            {isSubmitting || loading ? 'שומר...' : 'שמור שינויים'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditAdForm;

